import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface SeoPage {
  title: string;
  eyebrow: string;
  description: string;
  sections: { title: string; text: string }[];
}

export const SEO_CHAT_PAGES: Record<string, SeoPage> = {
  home: {
    eyebrow: "MEET SOMEONE NEW",
    title: "Chat with strangers online",
    description:
      "Talk to random strangers, make international friends, and start a respectful anonymous conversation through shared interests. No account required.",
    sections: [
      {
        title: "Talk to random people",
        text: "Start a friendly conversation with someone new in seconds, then skip whenever you are ready.",
      },
      {
        title: "Make international friends",
        text: "Meet people from around the world and discover new perspectives through shared interests.",
      },
      {
        title: "A simpler way to connect",
        text: "Choose a nickname, find a match, and keep your personal details private.",
      },
    ],
  },
  chat: {
    eyebrow: "TEXT CHAT",
    title: "Chat with strangers",
    description:
      "Start a free anonymous text chat with a random person who shares your interests.",
    sections: [
      {
        title: "Anonymous by default",
        text: "No account, no profile, no history. Your chat exists only for the duration of the conversation.",
      },
      {
        title: `Interest-based matching`,
        text: `Add topics you enjoy and ${APP_NAME} will prioritise matching you with someone who shares them.`,
      },
      {
        title: `Skip and move on`,
        text: "Not feeling it? Skip with one tap and find a new match instantly.",
      },
    ],
  },
  "how-it-works": {
    eyebrow: "HOW IT WORKS",
    title: "A simple way to meet someone new",
    description:
      "Choose a nickname, add a few interests, and start an anonymous text conversation with a random match.",
    sections: [
      {
        title: `Step 1 — Pick a nickname`,
        text: `No real name needed. Use any nickname or let ${APP_NAME} generate one for you.`,
      },
      {
        title: `Step 2 — Add your interests`,
        text: "Select topics like music, travel, or gaming. We use these to find someone on the same wavelength.",
      },
      {
        title: "Step 3 — Start talking",
        text: "A match is found in seconds. Type freely, skip anytime, and leave when you are done.",
      },
    ],
  },
  "chat-with-strangers": {
    eyebrow: "FREE ANONYMOUS TEXT CHAT",
    title: `Chat with strangers online — free, anonymous, no signup`,
    description: `Meet new people through free anonymous text chat on ${APP_NAME}. Interest-based matching, no account required, and full privacy controls in every conversation.`,
    sections: [
      {
        title: `No account needed — just start talking`,
        text: `Most chat platforms gate the experience behind signups, profile photos, and email verification. ${APP_NAME} skips all of that. Enter a display name, pick a few interests, and you are matched with someone new in seconds. There is nothing to install and nothing to set up.`,
      },
      {
        title: `Interest matching that breaks the ice`,
        text: `Starting a conversation with a stranger is easier when you already have something in common. ${APP_NAME} uses your listed interests to prioritise matches who share them, so the first message feels natural rather than forced. Add interests like music, travel, gaming, or any topic you enjoy and the algorithm does the rest.`,
      },
      {
        title: `Private by design, safe by default`,
        text: `Your real name, email, and contact details are never required. Every conversation is anonymous and messages are not stored on ${APP_NAME} servers after your session ends. The Skip button moves you on in one tap and Block keeps unwanted matches out of your future conversations.`,
      },
    ],
  },
  "random-chat": {
    eyebrow: "RANDOM CHAT WORLDWIDE",
    title: `Random chat with strangers online — meet people worldwide`,
    description: `Start a free random chat with strangers from around the world. Interest-based matching, anonymous text chat, and no account required on ${APP_NAME}.`,
    sections: [
      {
        title: `Meet someone new from anywhere in the world`,
        text: `${APP_NAME} connects you with people across every timezone and continent. Whether you want to hear about life in another city or find someone who shares a niche interest, there is always a new conversation waiting. The global pool means you are matched in seconds regardless of when you log on.`,
      },
      {
        title: `Random does not mean aimless`,
        text: `The best random chats start with a spark of common ground. Add a few interests — gaming, books, fitness, language learning — and ${APP_NAME} prioritises matching you with someone who shares them. You still meet someone new every time, but the conversation has a natural starting point from the first message.`,
      },
      {
        title: `Fully anonymous, every session`,
        text: `No profile, no saved history, and no data tied to your identity. Every session starts fresh. When you close the tab, the chat is gone — nothing persists on ${APP_NAME} servers. Your anonymity is the default, not a setting you need to switch on.`,
      },
    ],
  },
  "random-text-chat": {
    eyebrow: "TEXT CHAT",
    title: "Random text chat, made simple",
    description:
      "Start typing with a new person in a focused text chat. No camera, no public feed, and no account required.",
    sections: [
      {
        title: "Text-first, always",
        text: "No camera pressure. The conversation moves at the pace of words, giving both sides time to think.",
      },
      {
        title: "Fast to start",
        text: "A nickname and an interest list is all it takes. Matches appear in seconds.",
      },
      {
        title: "Skip freely",
        text: "If the conversation does not feel right, skip with one click and find a new match immediately.",
      },
    ],
  },
};
